/**
 * Header-Komponente mit Benutzer-Info und Navigation
 */

import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  styled,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  ButtonBase,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Storefront as StorefrontIcon,
  HelpOutline as HelpOutlineIcon,
  LockOutlined as LockOutlinedIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  BusinessStatusInfo,
  BusinessStatusMode,
  User,
} from "../../types/api-types";
import { useAuth } from "../../context/AuthContext";
import { apiClient } from "../../services/apiClient";

interface HeaderProps {
  user: User;
  onToggleSidebar?: () => void; // Make it optional since we only need it on mobile
}

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: "white",
  color: theme.palette.text.primary,
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  zIndex: theme.zIndex.drawer + 1,
}));

const StyledToolbar = styled(Toolbar)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0 24px",
  minHeight: "64px",
});

const BrandBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "16px",
});

const UserInfoBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  [theme.breakpoints.down("sm")]: {
    "& .user-details": {
      display: "none",
    },
  },
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  width: 40,
  height: 40,
  fontSize: "1.1rem",
  fontWeight: "bold",
}));

const Header: React.FC<HeaderProps> = ({ user, onToggleSidebar }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [businessStatus, setBusinessStatus] = useState<BusinessStatusInfo | null>(
    null
  );
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  const refreshBusinessStatus = async () => {
    try {
      setStatusLoading(true);
      const status = await apiClient.getBusinessStatus();
      setBusinessStatus(status);
    } catch (error) {
      console.error("Fehler beim Laden des Geschäftsstatus:", error);
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    refreshBusinessStatus();
    const interval = window.setInterval(refreshBusinessStatus, 60_000);
    return () => window.clearInterval(interval);
  }, []);

  const getUserInitials = () => {
    if (user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`;
    }
    return user.username.substring(0, 2).toUpperCase();
  };

  const getUserDisplayName = () => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.username;
  };

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case "admin":
        return "error";
      case "manager":
        return "warning";
      case "cashier":
      case "user":
        return "primary";
      default:
        return "default";
    }
  };

  const getEffectiveStatus = () => {
    return (
      businessStatus?.status ||
      (businessStatus?.isOpen ? BusinessStatusMode.OPEN : BusinessStatusMode.CLOSED)
    );
  };

  const getBusinessStatusLabel = () => {
    switch (getEffectiveStatus()) {
      case BusinessStatusMode.OPEN:
        return "Geöffnet";
      case BusinessStatusMode.REQUEST:
        return "Auf Anfrage";
      case BusinessStatusMode.CLOSED:
      default:
        return "Geschlossen";
    }
  };

  const getBusinessStatusColor = () => {
    switch (getEffectiveStatus()) {
      case BusinessStatusMode.OPEN:
        return "success";
      case BusinessStatusMode.REQUEST:
        return "warning";
      case BusinessStatusMode.CLOSED:
      default:
        return "error";
    }
  };

  const getBusinessStatusIcon = () => {
    switch (getEffectiveStatus()) {
      case BusinessStatusMode.OPEN:
        return <RadioButtonCheckedIcon fontSize="small" />;
      case BusinessStatusMode.REQUEST:
        return <HelpOutlineIcon fontSize="small" />;
      case BusinessStatusMode.CLOSED:
      default:
        return <LockOutlinedIcon fontSize="small" />;
    }
  };

  const getStatusOptions = () => {
    const currentStatus = getEffectiveStatus();
    return [
      BusinessStatusMode.OPEN,
      BusinessStatusMode.REQUEST,
      BusinessStatusMode.CLOSED,
    ].filter((status) => status !== currentStatus);
  };

  const handleBusinessStatusChange = async (status: BusinessStatusMode) => {
    try {
      setStatusLoading(true);
      setBusinessStatus((prev) =>
        prev ? { ...prev, status, isOpen: status === BusinessStatusMode.OPEN } : prev
      );
      await apiClient.setBusinessStatus({ status });
      await refreshBusinessStatus();
    } catch (error) {
      console.error("Fehler beim Setzen des Geschäftsstatus:", error);
      await refreshBusinessStatus();
    } finally {
      setStatusLoading(false);
      setStatusMenuAnchor(null);
    }
  };

  const handleProfileClick = () => {
    setUserMenuAnchor(null);
    navigate("/profile");
  };

  const handleLogoutClick = () => {
    setUserMenuAnchor(null);
    logout();
  };

  return (
    <StyledAppBar position="fixed">
      <StyledToolbar>
        <BrandBox>
          {isMobile && onToggleSidebar && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="toggle sidebar"
              onClick={onToggleSidebar}
              sx={{
                backgroundColor: "rgba(102, 126, 234, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(102, 126, 234, 0.2)",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ fontSize: "1.5rem" }}>🔧</Box>
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontWeight: 700,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: { xs: "none", sm: "block" },
              }}
            >
              Mietwerkstatt
            </Typography>
          </Box>
        </BrandBox>

        <UserInfoBox>
          <Tooltip title="Geschäftsstatus ändern">
            <ButtonBase
              component="button"
              type="button"
              onClick={(event) => {
                event.preventDefault();
                setStatusMenuAnchor(event.currentTarget as HTMLElement);
              }}
              sx={{
                borderRadius: 999,
                mr: 1,
                "&:focus-visible": {
                  outline: "2px solid #4c63d2",
                  outlineOffset: 2,
                },
              }}
              aria-haspopup="true"
              aria-controls="business-status-menu"
            >
              <Chip
                clickable
                label={getBusinessStatusLabel()}
                color={getBusinessStatusColor() as any}
                icon={
                  statusLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    getBusinessStatusIcon()
                  )
                }
                sx={{
                  cursor: "pointer",
                  fontWeight: 700,
                  "& .MuiChip-icon": {
                    color: "inherit",
                  },
                }}
              />
            </ButtonBase>
          </Tooltip>

          <ButtonBase
            className="user-details"
            onClick={handleProfileClick}
            sx={{
              textAlign: "right",
              mr: 1,
              borderRadius: 1,
              px: 0.75,
              py: 0.5,
              display: "block",
              "&:hover": {
                backgroundColor: "rgba(102, 126, 234, 0.08)",
              },
              "&:focus-visible": {
                outline: "2px solid #4c63d2",
                outlineOffset: 2,
              },
            }}
            aria-label="Profil öffnen"
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, lineHeight: 1.2 }}
            >
              {getUserDisplayName()}
            </Typography>
            <Chip
              label={user.role}
              size="small"
              color={getRoleColor(user.role) as any}
              variant="outlined"
              sx={{
                fontSize: "0.7rem",
                height: "20px",
                "& .MuiChip-label": {
                  padding: "0 6px",
                },
              }}
            />
          </ButtonBase>

          <Tooltip title="Profil">
            <IconButton
              color="inherit"
              onClick={(event) => setUserMenuAnchor(event.currentTarget)}
              sx={{ p: 0.5 }}
              aria-haspopup="true"
              aria-controls="user-menu"
            >
              <StyledAvatar>{getUserInitials()}</StyledAvatar>
            </IconButton>
          </Tooltip>

          <Tooltip title="Abmelden">
            <IconButton
              color="inherit"
              onClick={handleLogoutClick}
              sx={{
                ml: 1,
                backgroundColor: "rgba(244, 67, 54, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(244, 67, 54, 0.2)",
                },
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </UserInfoBox>
      </StyledToolbar>

      <Menu
        id="business-status-menu"
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {getStatusOptions().includes(BusinessStatusMode.OPEN) && (
          <MenuItem
            onClick={() => handleBusinessStatusChange(BusinessStatusMode.OPEN)}
          >
            <ListItemIcon>
              <StorefrontIcon fontSize="small" sx={{ color: "success.main" }} />
            </ListItemIcon>
            <ListItemText primary="Geöffnet" />
          </MenuItem>
        )}
        {getStatusOptions().includes(BusinessStatusMode.REQUEST) && (
          <MenuItem
            onClick={() =>
              handleBusinessStatusChange(BusinessStatusMode.REQUEST)
            }
          >
            <ListItemIcon>
              <HelpOutlineIcon fontSize="small" sx={{ color: "warning.main" }} />
            </ListItemIcon>
            <ListItemText primary="Auf Anfrage" />
          </MenuItem>
        )}
        {getStatusOptions().includes(BusinessStatusMode.CLOSED) && (
          <MenuItem
            onClick={() => handleBusinessStatusChange(BusinessStatusMode.CLOSED)}
          >
            <ListItemIcon>
              <LockOutlinedIcon fontSize="small" sx={{ color: "error.main" }} />
            </ListItemIcon>
            <ListItemText primary="Geschlossen" />
          </MenuItem>
        )}
      </Menu>

      <Menu
        id="user-menu"
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Profil" />
        </MenuItem>
        <MenuItem onClick={handleLogoutClick}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Abmelden" />
        </MenuItem>
      </Menu>
    </StyledAppBar>
  );
};

export default Header;
